// Attempt, response and evidence lifecycle (handoff §11–§13), independent of
// where records are stored. Every function is safe to call again after a
// timeout: it reads before it writes and never duplicates a record.
//
// An attempt is identified by its release (delivery + activity). The learner
// is the tablet's own, known only to the server side, which derives the
// deterministic sync_key from all three.

export const DRAWING_TYPE = "image/png";

// `currentStep` is the step's position in the activity, because that is what
// the attempt record stores. Step ids remain the app's own vocabulary, and are
// what `responses` is keyed by (handoff §12).
export const FIRST_STEP = 0;

const identity = (release) => ({
  deliveryId: release.deliveryId,
  activityId: release.activityId,
});

// Get-or-create. If the create fails (timeout, conflict, lost response), read
// again and resume what exists.
export const getOrCreateAttempt = async (repo, { release }) => {
  const existing = await repo.findAttempt(identity(release));
  if (existing) return existing;
  try {
    return await repo.createAttempt({
      ...identity(release),
      currentStep: FIRST_STEP,
    });
  } catch (error) {
    const created = await repo.findAttempt(identity(release));
    if (created) return created;
    throw error;
  }
};

// Reads an attempt without creating one: refreshing the waiting screen or
// "Your turn" must leave no trace.
export const findAttempt = (repo, { release }) =>
  repo.findAttempt(identity(release));

// Saves the meaningful resulting state of one step together with the step the
// learner moves on to. Writing the same state again is harmless.
export const saveStep = (repo, attempt, { stepId, response, nextStepIndex }) =>
  repo.updateAttempt(attempt.id, {
    responses: { ...attempt.responses, [stepId]: response },
    currentStep: nextStepIndex,
  });

// Links drawing evidence once. The repository looks for existing evidence,
// then for an already-uploaded file, before creating either, so a retry after
// a lost response never makes a second of each.
export const saveDrawingEvidence = (repo, attempt, { stepId, blob }) =>
  repo.saveDrawing({ attempt, stepId, blob });

// Saves one finished step. Picture work (a drawing or a tracing) is linked
// as evidence first, so the attempt never moves on past work it has not kept;
// an unmarked picture saves no file and no evidence, because a blank image is
// not meaningful work. The last step also completes the attempt.
export const finishStep = async (
  repo,
  attempt,
  { stepId, stepIndex, response, blob = null, last }
) => {
  if (blob) await saveDrawingEvidence(repo, attempt, { stepId, blob });
  return repo.updateAttempt(attempt.id, {
    responses: { ...attempt.responses, [stepId]: response },
    currentStep: last ? stepIndex : stepIndex + 1,
    ...(last
      ? { completedAt: attempt.completedAt || new Date().toISOString() }
      : {}),
  });
};

// A drawing that ends the activity.
export const finishDrawing = (repo, attempt, { stepId, stepIndex, blob }) =>
  finishStep(repo, attempt, {
    stepId,
    stepIndex,
    response: { completed: true },
    blob,
    last: true,
  });
